import { DeleteOutline, Edit } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ConfirmModal, Page, SortOrder, Table, useDebouncedState, useQueryParamsContext } from '../../../shared';
import { SearchInput } from '../../../shared/components/search/search.component';
import { IFoodRestriction } from '../../models';
import { useDeleteFoodRestriction, useFoodRestrictionsList } from '../../hooks/food-restriction.hook';

export const FoodRestrictionsPage: FC = () => {
    const { t } = useTranslation();
    const {
        queryParams: { page, pageSize, sort },
        setPage,
        setPageSize,
        setSort,
    } = useQueryParamsContext('food-restrictions', {
        sort: [{ field: 'name', sort: 'asc' }],
    });

    const [debouncedSearch, search, setSearch] = useDebouncedState('');

    const { isPending, data: foodRestrictions } = useFoodRestrictionsList({
        page,
        pageSize,
        sortBy: sort[0].field,
        sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
        search: debouncedSearch,
    });

    const { mutate: deleteFoodRestriction } = useDeleteFoodRestriction();

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('nameLabel'), minWidth: 150, flex: 1 },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 100,
            align: 'right',
            sortable: false,
            renderCell: ({ row: { id } }: GridCellParams<IFoodRestriction>) => (
                <>
                    <IconButton component={Link} to={`/admin/food-restrictions/${id}/edit`}>
                        <Edit />
                    </IconButton>
                    <ConfirmModal
                        onConfirm={() => {
                            deleteFoodRestriction({ id });
                        }}
                        button={
                            <IconButton aria-label="delete">
                                <DeleteOutline />
                            </IconButton>
                        }
                        title={t('foodRestrictionDeleteWarningTitle')}
                        text={t('foodRestrictionDeleteWarningText')}
                    />
                </>
            ),
        },
    ];

    return (
        <Page
            title={t('foodRestrictions')}
            actions={[
                <SearchInput value={search} setValue={setSearch} />,
                <Button component={Link} to="/admin/food-restrictions/new" color="primary" variant="contained">
                    {t('newFoodRestriction')}
                </Button>,
            ]}
        >
            <Table
                rows={foodRestrictions?.data || []}
                columns={columns}
                rowCount={foodRestrictions?.pagination?.size || 0}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={isPending}
                sortModel={sort}
                onSortModelChange={setSort}
            />
        </Page>
    );
};
